import { Link, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import { supabase } from "../services/supabase"
import { createLogger } from '../utils/logger'

const logger = createLogger('auth')

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const loginPath = "/login"

  const [ready, setReady] = useState(false)
  const [checking, setChecking] = useState(true)
  const [hashError, setHashError] = useState("")
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [done, setDone] = useState(false)

  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, "") || ""
    const params = new URLSearchParams(hash)
    const err = params.get("error_description") || params.get("error")
    if (err) {
      setHashError(decodeURIComponent(err.replace(/\+/g, " ")))
      setChecking(false)
      return
    }

    let cancelled = false

    const markReady = () => {
      if (!cancelled) {
        setReady(true)
        setChecking(false)
      }
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return
      if (session) markReady()
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) markReady()
      if (event === "SIGNED_IN" && session) markReady()
    })

    const t = window.setTimeout(() => {
      if (cancelled) return
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (cancelled) return
        if (session) markReady()
        else {
          setChecking(false)
        }
      })
    }, 500)

    return () => {
      cancelled = true
      window.clearTimeout(t)
      subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    if (password.length < 6) {
      setError("Password must be at least 6 characters.")
      return
    }
    if (password !== confirm) {
      setError("Passwords do not match.")
      return
    }
    setLoading(true)
    const { error: updateError } = await supabase.auth.updateUser({ password })
    setLoading(false)
    if (updateError) {
      logger.warn('Password update failed', { view: 'reset_password', action: 'update_password' }, updateError)
      setError(updateError.message)
      return
    }
    setDone(true)
    window.setTimeout(() => {
      navigate(loginPath, { replace: true })
    }, 2000)
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-10 block">
          <img
            src="/ls-logo.svg"
            alt="LandSentry"
            className="mx-auto h-14 w-auto md:h-16"
            width={280}
            height={70}
          />
        </Link>

        <div className="w-full max-w-sm rounded-lg border border-neutral-200 bg-white p-8 shadow-sm">
          <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Set new password</h1>

          {hashError && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
              {hashError}
            </div>
          )}

          {checking && !hashError && (
            <p className="mt-4 text-sm text-neutral-500">Verifying reset link…</p>
          )}

          {!checking && !hashError && !ready && (
            <div className="mt-4 text-sm text-neutral-700">
              <p>This reset link is invalid or has expired. Request a new one from the sign-in page.</p>
              <p className="mt-4">
                <Link to={loginPath} className="font-medium text-neutral-900 underline">
                  Back to sign in
                </Link>
              </p>
            </div>
          )}

          {ready && !done && (
            <>
              <p className="mt-1 text-sm text-neutral-500">Choose a new password for your account.</p>
              {error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="New password"
                  className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900"
                />
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={loading}
                  placeholder="Confirm new password"
                  className="w-full rounded-md border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
                >
                  {loading ? (
                    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    "Update password"
                  )}
                </button>
              </form>
            </>
          )}

          {done && (
            <p className="mt-4 text-sm text-green-800">Password updated. Redirecting to sign in…</p>
          )}

          {!done && ready && (
            <p className="mt-6 text-center text-sm">
              <Link to={loginPath} className="text-neutral-600 hover:text-neutral-900">
                Cancel
              </Link>
            </p>
          )}
        </div>

        <Link to="/" className="mt-10 text-sm text-neutral-500 hover:text-neutral-900">
          ← Back to home
        </Link>
      </div>
    </div>
  )
}
